import { useState } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import type { FieldOption } from '../../promotions/types';

interface ChatAutocompleteInputProps {
  options: FieldOption[];
  multiple: boolean;
  disabled: boolean;
  onSend: (value: string) => void;
}

const filter = createFilterOptions<FieldOption>();

export default function ChatAutocompleteInput({ options, multiple, disabled, onSend }: ChatAutocompleteInputProps) {
  const [selected, setSelected] = useState<FieldOption[]>([]);

  const handleSend = () => {
    if (disabled || selected.length === 0) return;
    const value = selected.map((s) => s.value).join(',');
    setSelected([]);
    onSend(value);
  };

  if (multiple) {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', width: '100%' }}>
        <Autocomplete
          multiple
          freeSolo
          size="small"
          sx={{ flex: 1 }}
          disabled={disabled}
          options={options}
          value={selected}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
          isOptionEqualToValue={(opt, val:any) => opt.value === val.value}
          filterOptions={(opts, params) => {
            const filtered = filter(opts, params);
            const inputValue = params.inputValue.trim();
            if (
              inputValue &&
              !opts.some((o) => o.value === inputValue || o.label.toLowerCase() === inputValue.toLowerCase())
            ) {
              filtered.push({ value: inputValue, label: `Adicionar "${inputValue}"` });
            }
            return filtered;
          }}
          onChange={(_e, newValue) => {
            const resolved = newValue.map((v): FieldOption => {
              if (typeof v === 'string') return { value: v, label: v };
              if (v.label.startsWith('Adicionar "')) return { value: v.value, label: v.value };
              return v;
            });
            setSelected(resolved);
          }}
          renderValue={(tagValues, getTagProps) =>
            tagValues.map((opt, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={typeof opt === 'string' ? opt : opt.label}
                  size="small"
                  {...tagProps}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={disabled ? 'Aguarde...' : 'Selecione ou digite...'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.875rem',
                },
              }}
            />
          )}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={disabled || selected.length === 0}
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            width: 36,
            height: 36,
            flexShrink: 0,
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.400' },
          }}
        >
          <SendIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', width: '100%' }}>
      <Autocomplete
        freeSolo
        size="small"
        sx={{ flex: 1 }}
        disabled={disabled}
        options={options}
        value={selected[0] ?? null}
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
        isOptionEqualToValue={(opt, val) => {
          if (typeof opt === 'string' || typeof val === 'string') return false;
          return opt.value === val.value;
        }}
        filterOptions={(opts, params) => {
          const filtered = filter(opts, params);
          const inputValue = params.inputValue.trim();
          if (
            inputValue &&
            !opts.some((o) => o.value === inputValue || o.label.toLowerCase() === inputValue.toLowerCase())
          ) {
            filtered.push({ value: inputValue, label: `Adicionar "${inputValue}"` });
          }
          return filtered;
        }}
        onChange={(_e, newValue) => {
          if (!newValue) { setSelected([]); return; }
          if (typeof newValue === 'string') {
            setSelected([{ value: newValue, label: newValue }]);
          } else if (newValue.label.startsWith('Adicionar "')) {
            setSelected([{ value: newValue.value, label: newValue.value }]);
          } else {
            setSelected([newValue]);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={disabled ? 'Aguarde...' : 'Selecione ou digite...'}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.875rem',
              },
            }}
          />
        )}
      />
      <IconButton
        color="primary"
        onClick={handleSend}
        disabled={disabled || selected.length === 0}
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          width: 36,
          height: 36,
          flexShrink: 0,
          '&:hover': { bgcolor: 'primary.dark' },
          '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.400' },
        }}
      >
        <SendIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}
